import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Head } from "zudoku/components";
import { Alert, AlertDescription, AlertTitle } from "zudoku/ui/Alert";
import { Accordion } from "zudoku/ui/Accordion";
import { Skeleton } from "zudoku/ui/Skeleton";
import { useZudoku } from "zudoku/hooks";
import { Plus } from "zudoku/icons";
import {
	CreateAppDialog,
	type AppFormValues,
} from "../components/CreateAppDialog";
import { CreateKeyDialog } from "../components/CreateKeyDialog";
import { AppCard } from "../components/AppCard";
import { createApiManagementClient } from "../services/apiManagementService";
import type {
	ApiKey,
	CreateApiKeyRequest,
	OrgApp,
} from "../types/apiManagement";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "zudoku/ui/AlertDialog";

type AppWithState = OrgApp & {
	keys: ApiKey[];
	isLoadingKeys: boolean;
	keysError?: string | null;
};

const toErrorMessage = (error: unknown): string => {
	if (!error) return "An unknown error occurred.";
	if (typeof error === "string") return error;
	if (error instanceof Error) return error.message;
	if (
		typeof error === "object" &&
		"message" in error &&
		typeof (error as any).message === "string"
	) {
		return (error as any).message;
	}
	return JSON.stringify(error);
};

const ApiKeysPage = () => {
	const { signRequest } = useZudoku();
	const client = useMemo(
		() => createApiManagementClient(signRequest),
		[signRequest],
	);

	const [apps, setApps] = useState<AppWithState[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [pageError, setPageError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const [isCreateAppOpen, setIsCreateAppOpen] = useState(false);
	const [isEditAppOpen, setIsEditAppOpen] = useState(false);
	const [appForEdit, setAppForEdit] = useState<OrgApp | null>(null);
	const [appPendingDelete, setAppPendingDelete] = useState<OrgApp | null>(null);
	const [keyPendingDelete, setKeyPendingDelete] = useState<{
		app: OrgApp;
		key: ApiKey;
	} | null>(null);

	const [isCreateKeyOpen, setIsCreateKeyOpen] = useState(false);
	const [appForKey, setAppForKey] = useState<OrgApp | null>(null);
	const [createdKey, setCreatedKey] = useState<{
		name: string;
		value: string;
	} | null>(null);

	const [isCreatingApp, setIsCreatingApp] = useState(false);
	const [isUpdatingApp, setIsUpdatingApp] = useState(false);
	const [isDeletingApp, setIsDeletingApp] = useState(false);
	const [isCreatingKey, setIsCreatingKey] = useState(false);
	const [isDeletingKey, setIsDeletingKey] = useState(false);

	const resetKeyDialogState = useCallback(() => {
		setCreatedKey(null);
		setAppForKey(null);
		setIsCreateKeyOpen(false);
	}, []);

	const updateAppState = useCallback(
		(
			appId: string,
			updater: (app: AppWithState) => AppWithState,
			fallback?: () => void,
		) => {
			setApps((prev) => {
				const exists = prev.some((app) => app.app_id === appId);
				if (!exists) {
					if (fallback) fallback();
					return prev;
				}
				return prev.map((app) => (app.app_id === appId ? updater(app) : app));
			});
		},
		[],
	);

	const refreshAppKeys = useCallback(
		async (app: OrgApp) => {
			updateAppState(app.app_id, (current) => ({
				...current,
				isLoadingKeys: true,
				keysError: null,
			}));

			try {
				const keys = await client.fetchAppKeys(app.app_id);
				updateAppState(app.app_id, (current) => ({
					...current,
					keys,
					isLoadingKeys: false,
					keysError: null,
				}));
			} catch (error) {
				updateAppState(app.app_id, (current) => ({
					...current,
					isLoadingKeys: false,
					keysError: toErrorMessage(error),
				}));
			}
		},
		[client, updateAppState],
	);

	const loadApps = useCallback(async () => {
		setIsLoading(true);
		setPageError(null);

		try {
			const fetchedApps = await client.fetchApps();
			setApps(
				fetchedApps.map((app) => ({
					...app,
					keys: [],
					isLoadingKeys: true,
					keysError: null,
				})),
			);

			await Promise.all(
				fetchedApps.map(async (app) => {
					try {
						const keys = await client.fetchAppKeys(app.app_id);
						updateAppState(app.app_id, (current) => ({
							...current,
							keys,
							isLoadingKeys: false,
							keysError: null,
						}));
					} catch (error) {
						updateAppState(app.app_id, (current) => ({
							...current,
							isLoadingKeys: false,
							keysError: toErrorMessage(error),
						}));
					}
				}),
			);
		} catch (error) {
			setPageError(toErrorMessage(error));
			setApps([]);
		} finally {
			setIsLoading(false);
		}
	}, [client, updateAppState]);

	useEffect(() => {
		void loadApps();
	}, [loadApps]);

	const handleCreateApp = async ({
		name,
		description,
		privacyUrl,
	}: AppFormValues) => {
		setIsCreatingApp(true);
		setActionError(null);

		try {
			await client.createApp({
				name,
				description: description ?? null,
				privacy_url: privacyUrl ?? null,
			});
			setIsCreateAppOpen(false);
			await loadApps();
		} catch (error) {
			setActionError(toErrorMessage(error));
		} finally {
			setIsCreatingApp(false);
		}
	};

	const handleEditApp = async ({
		description,
		privacyUrl,
	}: AppFormValues) => {
		if (!appForEdit) return;
		setIsUpdatingApp(true);
		setActionError(null);

		try {
			await client.updateApp({
				app_id: appForEdit.app_id,
				description: description ?? null,
				privacy_url: privacyUrl ?? null,
			});

			updateAppState(appForEdit.app_id, (current) => ({
				...current,
				description: description ?? null,
				privacy_url: privacyUrl ?? null,
			}));

			setIsEditAppOpen(false);
			setAppForEdit(null);
		} catch (error) {
			setActionError(toErrorMessage(error));
		} finally {
			setIsUpdatingApp(false);
		}
	};

	const handleDeleteApp = async () => {
		if (!appPendingDelete?.name) {
			setActionError("Unable to delete app: Missing app name.");
			return;
		}

		setIsDeletingApp(true);
		setActionError(null);

		try {
			await client.deleteApp({ name: appPendingDelete.name });
			setApps((prev) =>
				prev.filter((app) => app.app_id !== appPendingDelete.app_id),
			);
			setAppPendingDelete(null);
		} catch (error) {
			setActionError(toErrorMessage(error));
		} finally {
			setIsDeletingApp(false);
		}
	};

	const handleCreateKey = async ({
		keyName,
		expiry,
	}: {
		keyName: string;
		expiry: CreateApiKeyRequest["expiry"];
	}) => {
		if (!appForKey?.name) {
			setActionError("Unable to create API key: Missing app name.");
			return;
		}

		setIsCreatingKey(true);
		setActionError(null);

		try {
			const response = await client.createApiKey({
				app_name: appForKey.name,
				key_name: keyName,
				expiry,
			});

			const raw = response as unknown as Record<string, unknown>;
			const rawKey = raw?.key as unknown;
			const generatedValue =
				(typeof raw?.plaintext_key === "string"
					? raw.plaintext_key
					: undefined) ??
				(typeof raw?.plaintextKey === "string"
					? raw.plaintextKey
					: undefined) ??
				(typeof raw?.api_key === "string" ? raw.api_key : undefined) ??
				(typeof rawKey === "string" ? rawKey : undefined) ??
				(typeof rawKey === "object" &&
				rawKey !== null &&
				typeof (rawKey as Record<string, unknown>).key === "string"
					? ((rawKey as Record<string, unknown>).key as string)
					: undefined) ??
				"";

			const createdKeyName =
				(typeof rawKey === "object" &&
				rawKey !== null &&
				typeof (rawKey as Record<string, unknown>).name === "string"
					? ((rawKey as Record<string, unknown>).name as string)
					: undefined) ?? keyName;

			await refreshAppKeys(appForKey);

			if (generatedValue.trim()) {
				setCreatedKey({ name: createdKeyName, value: generatedValue.trim() });
			} else {
				resetKeyDialogState();
			}
		} catch (error) {
			setActionError(toErrorMessage(error));
		} finally {
			setIsCreatingKey(false);
		}
	};

	const handleDeleteKey = async () => {
		if (!keyPendingDelete) return;

		const {
			app,
			key: { name, id },
		} = keyPendingDelete;

		const keyName = name || id;
		if (!keyName) {
			setActionError("Unable to delete API key: Missing key identifier.");
			return;
		}

		setIsDeletingKey(true);
		setActionError(null);

		try {
			await client.deleteApiKey({
				key_name: keyName,
				app_id: app.app_id,
			});
			await refreshAppKeys(app);
			setKeyPendingDelete(null);
		} catch (error) {
			setActionError(toErrorMessage(error));
		} finally {
			setIsDeletingKey(false);
		}
	};

	const showEmptyState = !isLoading && apps.length === 0 && !pageError;

	return (
		<section className="my-8 px-48 space-y-6">
			<Head>
				<title>API Keys</title>
			</Head>

			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
					<p className="text-sm text-muted-foreground">
						Manage Apps and API keys for your organization.
					</p>
				</div>
				<Button
					className="inline-flex items-center gap-2"
					onClick={() => setIsCreateAppOpen(true)}
				>
					<Plus size={16} />
					Create app
				</Button>
			</div>

			{pageError ? (
				<Alert variant="destructive">
					<AlertTitle>Unable to load apps</AlertTitle>
					<AlertDescription>{pageError}</AlertDescription>
				</Alert>
			) : null}

			{actionError ? (
				<Alert variant="destructive">
					<AlertTitle>Action failed</AlertTitle>
					<AlertDescription>{actionError}</AlertDescription>
				</Alert>
			) : null}

			{isLoading && apps.length === 0 ? (
				<div className="space-y-3">
					{Array.from({ length: 3 }).map((_, index) => (
						<div
							key={`app-skeleton-${index}`}
							className="rounded-lg border border-dashed bg-muted/40 p-6"
						>
							<Skeleton className="h-6 w-48" />
							<div className="mt-4 space-y-2">
								<Skeleton className="h-4 w-3/4" />
								<Skeleton className="h-4 w-1/2" />
							</div>
						</div>
					))}
				</div>
			) : null}

			{showEmptyState ? (
				<div className="rounded-lg border border-dashed bg-muted/40 p-10 text-center">
					<h2 className="text-lg font-medium">No apps yet</h2>
					<p className="mt-2 text-sm text-muted-foreground">
						Create your first app to start generating API keys.
					</p>
					<Button className="mt-4" onClick={() => setIsCreateAppOpen(true)}>
						Create app
					</Button>
				</div>
			) : null}

			{apps.length > 0 ? (
				<Accordion type="multiple" className="space-y-4">
					{apps.map((app) => (
						<AppCard
							key={app.app_id}
							app={app}
							keys={app.keys}
							isLoadingKeys={app.isLoadingKeys}
							keysError={app.keysError}
							onCreateKey={(selected) => {
								setAppForKey(selected);
								setIsCreateKeyOpen(true);
							}}
							onEditApp={(selected) => {
								setAppForEdit(selected);
								setIsEditAppOpen(true);
							}}
							onDeleteApp={(selected) => setAppPendingDelete(selected)}
							onDeleteKey={(selectedApp, key) =>
								setKeyPendingDelete({ app: selectedApp, key })
							}
							onRefreshKeys={refreshAppKeys}
						/>
					))}
				</Accordion>
			) : null}

			<CreateAppDialog
				open={isCreateAppOpen}
				onOpenChange={setIsCreateAppOpen}
				onSubmit={handleCreateApp}
				isSubmitting={isCreatingApp}
				ctaLabel="Create app"
			/>

			<CreateAppDialog
				open={isEditAppOpen}
				onOpenChange={(open) => {
					setIsEditAppOpen(open);
					if (!open) {
						setAppForEdit(null);
					}
				}}
				onSubmit={handleEditApp}
				isSubmitting={isUpdatingApp}
				initialValues={{
					name: appForEdit?.name ?? "",
					description: appForEdit?.description ?? "",
					privacyUrl: appForEdit?.privacy_url ?? "",
				}}
				title="Edit App"
				description="Update the app details."
				ctaLabel="Save changes"
				readOnlyName
			/>

			<CreateKeyDialog
				open={isCreateKeyOpen}
				onOpenChange={(open) => {
					if (!open) {
						resetKeyDialogState();
					} else {
						setIsCreateKeyOpen(open);
					}
				}}
				onSubmit={handleCreateKey}
				isSubmitting={isCreatingKey}
				appName={appForKey?.name}
				createdKey={createdKey ?? undefined}
				onReset={resetKeyDialogState}
			/>

			<AlertDialog
				open={!!appPendingDelete}
				onOpenChange={(open) => {
					if (!open) setAppPendingDelete(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete app</AlertDialogTitle>
						<AlertDialogDescription>
							This will delete {appPendingDelete?.name} and all associated API
							keys. This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingApp}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteApp}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							disabled={isDeletingApp}
						>
							{isDeletingApp ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!keyPendingDelete}
				onOpenChange={(open) => {
					if (!open) setKeyPendingDelete(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete API key</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete the key{" "}
							<span className="font-medium">
								{keyPendingDelete?.key.name || keyPendingDelete?.key.id}
							</span>
							? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingKey}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteKey}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							disabled={isDeletingKey}
						>
							{isDeletingKey ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</section>
	);
};

export default ApiKeysPage;
